import { Suspense, useEffect, useState } from 'react';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import * as Sentry from '@sentry/react';
import { Analytics } from 'lib/AnalyticsLoader/analytics';
import { AudioProvider } from 'lib/audio/audio-context';

import { useGameStatusStore } from 'modules/Room/game-status/store/game-status-store';
import { Room } from 'modules/Room/pages/Room';
import { DailyQuestsPage } from 'modules/daily-quests-page/daily-quests-page';
import { useQuests } from 'modules/daily-quests-page/hooks/use-quests';
import { ExploreGames } from 'modules/explore-games/pages/explore-games';
import { HomePage } from 'modules/landing-page/pages';
import { useLeaderboard } from 'modules/leaderboard/hooks/use-leaderboard';
import { LeaderBoardPage } from 'modules/leaderboard/pages/leaderboard';
import { LobbyPage } from 'modules/lobby-page/pages';
import { NftCabinet } from 'modules/nfts/nft-cabinet';
import { PrizeBoxPage } from 'modules/prize-box-page/prize-box-page';
import { usePrizeBoxStore } from 'modules/prize-box-page/store/prize-box-store';
import { StreamRoom } from 'modules/stream-room-page';
import { Verify } from 'modules/verify/verify';

import ErrorBoundary from 'shared/components/sections/ErrorBoundary/ErrorBoundary';
import GridBackground from 'shared/components/ui/GridBackground/GridBackground';
import { ROUTES } from 'shared/constants';
import { useAccount, useAccountEffect, usePublicClient } from 'shared/hooks/use-web3';
import { useGetURLRef } from 'shared/hooks/useGetURLRef';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { usePlayerGamesStore } from 'shared/store/playerGamesStore';
import { useQuestsStore } from 'shared/store/questsStore';
import { useRoomStore } from 'shared/store/roomStore';
import { useStartGameStore } from 'shared/store/start-game-store';
import { useUIStore } from 'shared/store/uiStore';
import { isMocked } from 'shared/utils/isMocked';

import './base.css';

const App = () => {
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const { address, chainId, isConnected } = useAccount();
  const { invitedBy } = useGlobalStore();
  const resetRoomStore = useRoomStore((state) => state.resetStore);
  const { setConnectWalletModalOpen } = useUIStore();

  const { refreshPrizeBoxStore } = usePrizeBoxStore();
  const { fetchUserBalances } = useQuestsStore();
  const { refresh: refreshQuests } = useQuests();
  const publicClient = usePublicClient();
  const location = useLocation();

  const streamMatch = matchPath('/stream/:networkId/:activeRoomId', location.pathname);
  const roomMatch = matchPath('/room/:networkId/:activeRoomId', location.pathname);
  const urlChainId = Number(streamMatch?.params.networkId ?? roomMatch?.params.networkId);

  const { startBalancePolling, stopBalancePolling, reset, selectedCurrency } = useCurrenciesStore();

  // Set Sentry user context on address/chainId change
  useEffect(() => {
    if (address) {
      Sentry.setUser({
        id: address.toString(),
        chainId: chainId,
        buildVersion: process.env.APP_VERSION,
      });
    }
  }, [address, chainId]);

  useGetURLRef();

  useEffect(() => {
    if (address && chainId && publicClient) {
      startBalancePolling(address, publicClient, chainId);
    }

    return () => stopBalancePolling();
  }, [address, chainId, publicClient, startBalancePolling, stopBalancePolling]);

  useEffect(() => {
    if (isConnected && chainId) {
      fetchUserBalances(publicClient, address, true);
      refreshPrizeBoxStore(publicClient, address);
      refreshQuests();
    }
  }, [chainId, isConnected, publicClient, address]);

  useEffect(() => {
    if (isWalletConnected) {
      Analytics.handleUserId(address);
      Analytics.connectWalletEvent(
        address,
        invitedBy,
        selectedCurrency?.balance,
        selectedCurrency?.symbol,
      );
    }
  }, [isWalletConnected, address, invitedBy, selectedCurrency]);

  useAccountEffect({
    onConnect() {
      const alreadyConnected = sessionStorage.getItem('walletConnected');
      if (!alreadyConnected) {
        setIsWalletConnected(true);
      }
      sessionStorage.setItem('walletConnected', 'true');
    },
    onDisconnect() {
      // Clear wallet-derived UI state; the app will fall back to Base
      resetRoomStore();
      setConnectWalletModalOpen(false);
      reset();
      // No reload; render continues on DEFAULT_CHAIN
    },
  });

  useEffect(() => {
    if (isMocked) {
      if (typeof window !== 'undefined' && window.location.search.includes('mock=true')) {
        (window as any).useRoomStore = useRoomStore;
        (window as any).useGameStatusStore = useGameStatusStore;
        (window as any).usePlayerGamesStore = usePlayerGamesStore;
        (window as any).useGlobalStore = useGlobalStore;
        (window as any).useStartGameStore = useStartGameStore;
      }
    }
  }, []);

  useEffect(() => {
    const urlSearchParams = sessionStorage.getItem('urlSearchParams');
    if (!urlSearchParams) {
      const url = new URL(window.location.href);
      const params: Record<string, any> = {};
      url.searchParams.forEach((value, key) => {
        params[key] = value;
      });
      if (Object.keys(params).length) {
        sessionStorage.setItem('urlSearchParams', JSON.stringify(params));
      }
    }
  }, []);
  useLeaderboard();

  useEffect(() => {
    if (isConnected) {
      setConnectWalletModalOpen(false);
    }
  }, [isConnected, setConnectWalletModalOpen]);

  return (
    <AudioProvider>
      <GridBackground>
        <Suspense fallback={<div className="h-screen w-full bg-dark-background" />}>
          <ErrorBoundary>
            <Routes>
              <Route element={<Room />} path={`${ROUTES.ROOM}/:networkId/:activeRoomId?`} />
              <Route element={<LobbyPage />} path={`${ROUTES.LOBBY}/:invitedAddress?`} />
              <Route element={<HomePage />} path={ROUTES.HOME} />
              <Route element={<ExploreGames />} path={ROUTES.EXPLORE_GAMES} />
              <Route element={<DailyQuestsPage />} path={ROUTES.DAILY_QUESTS} />
              <Route element={<StreamRoom />} path={`${ROUTES.STREAM}/:networkId/:activeRoomId`} />
              <Route element={<Verify />} path={`${ROUTES.VERIFY}/:discordId`} />
              <Route element={<NftCabinet />} path={ROUTES.NFTS} />
              <Route element={<LeaderBoardPage />} path={ROUTES.LEADERBOARD} />
              <Route element={<PrizeBoxPage />} path={ROUTES.PRIZE_BOX} />
              <Route path="*" element={<Navigate to={ROUTES.HOME} />} />
            </Routes>
          </ErrorBoundary>
        </Suspense>
      </GridBackground>
    </AudioProvider>
  );
};

export default App;
